import { cn } from "./classNames-BzrkDnOD.js";
import { useArgoDesign } from "./context-U293r3v-.js";
import { Check, ChevronDown, ChevronUp } from "./icons-wcA70iQx.js";
import { Button } from "./Button-B21oVcPU.js";
import { overlayTriggerVariants } from "./styles-gzcjOyKv.js";
import { forwardAccessibleName } from "./internal/utils/accessibleName.js";
import { useId } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Root } from "@radix-ui/react-select";
//#region src/components/Select/SelectContent.tsx
const scrollButtonStyles = cn("tw:flex", "tw:items-center", "tw:justify-center", "tw:rounded-sm", "tw:p-1", "tw:[&_svg:not([class*=\"tw:size-\"])]:size-5");
/**
* Listbox container for `Select`. Portals items into a popover sized to the trigger.
* Normally rendered implicitly by `Select`; use directly only when composing custom item layouts via
* the `Select.Root` / `Select.Trigger` / `Select.Content` compound parts.
*/
const SelectContent = ({ ref, className, children, container, position = "popper", sideOffset = 4, ...props }) => {
	const effectiveVariant = useArgoDesign() ? "argo" : "default";
	return /* @__PURE__ */ jsx(SelectPrimitive.Portal, {
		container,
		children: /* @__PURE__ */ jsxs(SelectPrimitive.Content, {
			...props,
			ref,
			position,
			sideOffset,
			"data-variant": effectiveVariant,
			className: cn("tailwindcss-preflight", "tw:group/select-content", "tw:z-[1055]", "tw:flex tw:flex-col", "tw:p-1", "tw:data-[variant=default]:p-0", "tw:overflow-hidden", "tw:origin-(--radix-select-content-transform-origin)", "tw:max-h-[var(--radix-select-content-available-height)]", "tw:min-w-[var(--radix-select-trigger-width)]", "tw:bg-popover", "tw:text-popover-foreground", "tw:shadow-md", "tw:ring-1", "tw:ring-foreground/10", "tw:rounded-lg", "tw:data-[variant=default]:rounded", "tw:data-[state=open]:animate-in", "tw:data-[state=open]:fade-in-0", "tw:data-[state=open]:zoom-in-95", "tw:data-[state=closed]:animate-out", "tw:data-[state=closed]:fade-out-0", "tw:data-[state=closed]:zoom-out-95", "tw:data-[side=bottom]:slide-in-from-top-2", "tw:data-[side=left]:slide-in-from-right-2", "tw:data-[side=right]:slide-in-from-left-2", "tw:data-[side=top]:slide-in-from-bottom-2", className),
			children: [
				/* @__PURE__ */ jsx(SelectPrimitive.ScrollUpButton, {
					className: scrollButtonStyles,
					children: /* @__PURE__ */ jsx(ChevronUp, {})
				}),
				/* @__PURE__ */ jsx(SelectPrimitive.Viewport, {
					className: cn({ "tw:h-[var(--radix-select-trigger-height)] tw:w-full tw:min-w-[var(--radix-select-trigger-width)]": position === "popper" }),
					children
				}),
				/* @__PURE__ */ jsx(SelectPrimitive.ScrollDownButton, {
					className: scrollButtonStyles,
					children: /* @__PURE__ */ jsx(ChevronDown, {})
				})
			]
		})
	});
};
//#endregion
//#region src/components/Select/SelectGroup.tsx
const SelectGroup = ({ className, ...props }) => /* @__PURE__ */ jsx(SelectPrimitive.Group, {
	...props,
	className: cn("tw:flex tw:flex-col tw:gap-0.5", className)
});
//#endregion
//#region src/components/Select/SelectItem.tsx
const SelectItem = ({ ref, className, children, icon, hint, "aria-describedby": ariaDescribedBy, ...props }) => {
	const hintId = useId();
	return /* @__PURE__ */ jsxs(SelectPrimitive.Item, {
		...props,
		ref,
		"aria-describedby": [ariaDescribedBy, hint ? hintId : null].filter(Boolean).join(" ") || void 0,
		className: cn("tw:group/select-item", "tw:relative", "tw:flex", "tw:flex-1", "tw:items-center", "tw:gap-2", "tw:py-2", "tw:px-2", "tw:group-data-[variant=default]/select-content:px-4", "tw:rounded-lg", "tw:group-data-[variant=default]/select-content:rounded-none", "tw:text-sm", "tw:select-none", "tw:cursor-default", "tw:outline-hidden", "tw:focus:outline-hidden", "tw:data-[highlighted]:bg-accent", "tw:group-data-[variant=default]/select-content:data-[highlighted]:bg-secondary", "tw:data-[highlighted]:text-accent-foreground", "tw:group-data-[variant=default]/select-content:data-[highlighted]:text-secondary-foreground", "tw:data-[disabled]:pointer-events-none", "tw:data-[disabled]:opacity-50", "tw:[&_svg]:pointer-events-none", "tw:[&_svg]:shrink-0", "tw:[&_svg]:text-muted-foreground", "tw:[&_svg:not([class*='tw:size-'])]:size-4", className),
		children: [
			icon,
			/* @__PURE__ */ jsx("span", {
				className: "tw:flex-1 tw:truncate",
				children: /* @__PURE__ */ jsx(SelectPrimitive.ItemText, { children })
			}),
			hint && /* @__PURE__ */ jsx("span", {
				id: hintId,
				className: "tw:shrink-0 tw:text-xs tw:text-muted-foreground",
				children: hint
			}),
			/* @__PURE__ */ jsx(SelectPrimitive.ItemIndicator, {
				className: "tw:pointer-events-none",
				children: /* @__PURE__ */ jsx(Check, { className: "tw:size-4" })
			})
		]
	});
};
//#endregion
//#region src/components/Select/SelectLabel.tsx
const SelectLabel = ({ className, ...props }) => /* @__PURE__ */ jsx(SelectPrimitive.Label, {
	...props,
	className: cn("tw:px-2", "tw:py-1.5", "tw:text-xs", "tw:font-medium", "tw:leading-none", "tw:text-muted-foreground", "tw:group-data-[variant=default]/select-content:uppercase", "tw:group-data-[variant=default]/select-content:font-semibold", "tw:group-data-[variant=default]/select-content:tracking-wider", "tw:group-data-[variant=default]/select-content:text-[11px]", "tw:group-data-[variant=default]/select-content:text-black", "tw:group-data-[variant=default]/select-content:pt-2", "tw:group-data-[variant=default]/select-content:px-4", className)
});
//#endregion
//#region src/components/Select/SelectRoot.tsx
const SelectRoot = (props) => /* @__PURE__ */ jsx(Root, { ...props });
//#endregion
//#region src/components/Select/SelectSeparator.tsx
const SelectSeparator = ({ className, ...props }) => /* @__PURE__ */ jsx(SelectPrimitive.Separator, {
	...props,
	className: cn("tw:mx-2 tw:my-1 tw:h-px tw:bg-border", className)
});
//#endregion
//#region src/components/Select/SelectTrigger.tsx
const SelectTrigger = ({ ref, className, variant, state, sizing, placeholder, ...props }) => {
	const isArgoDesign = useArgoDesign();
	const effectiveVariant = (() => {
		if (variant === "inline") return "inline";
		if (isArgoDesign) return "argo";
		return variant;
	})();
	if (effectiveVariant === "inline") return /* @__PURE__ */ jsx(SelectPrimitive.Trigger, {
		...props,
		ref,
		className: cn(className),
		asChild: true,
		children: /* @__PURE__ */ jsxs(Button, {
			variant: "inline",
			children: [/* @__PURE__ */ jsx(SelectPrimitive.Value, { placeholder }), /* @__PURE__ */ jsx(SelectPrimitive.Icon, {
				asChild: true,
				children: /* @__PURE__ */ jsx(ChevronDown, {})
			})]
		})
	});
	return /* @__PURE__ */ jsxs(SelectPrimitive.Trigger, {
		...props,
		ref,
		className: cn(overlayTriggerVariants({
			variant: effectiveVariant,
			state,
			sizing
		}), className),
		children: [/* @__PURE__ */ jsx(SelectPrimitive.Value, { placeholder }), /* @__PURE__ */ jsx(SelectPrimitive.Icon, {
			asChild: true,
			children: /* @__PURE__ */ jsx(ChevronDown, { className: "tw:size-4 tw:text-muted-foreground" })
		})]
	});
};
//#endregion
//#region src/components/Select/Select.tsx
const isGroup = (itemOrGroup) => "items" in itemOrGroup;
/**
* Single selection from a short enumerated list. Built on Radix Select.
*
* - Use for ~10 or fewer options where search adds nothing; else `Combobox` / `MultiCombobox`.
* - In forms: `FormGroup`, or `useAppForm` + `field.SelectField` for validation.
* - Compound parts (`Select.Root`, `.Trigger`, `.Content`, `.Item`) for custom item layouts.
*/
const Select = ({ value, defaultValue, onValueChange, disabled, name, required, open, defaultOpen, onOpenChange, dir, autoComplete, form, variant, state, sizing, placeholder, className, onBlur, "aria-describedby": ariaDescribedBy, "aria-invalid": ariaInvalid, "aria-required": ariaRequired, items = [], ...accessibleName }) => {
	const groupKeyId = useId();
	const renderItem = (item) => /* @__PURE__ */ jsx(SelectItem, {
		value: item.value,
		icon: item.icon,
		hint: item.hint,
		disabled: item.disabled,
		children: item.label
	}, item.value);
	return /* @__PURE__ */ jsxs(SelectRoot, {
		value,
		defaultValue,
		onValueChange,
		disabled,
		name,
		required,
		open,
		defaultOpen,
		onOpenChange,
		dir,
		autoComplete,
		form,
		children: [/* @__PURE__ */ jsx(SelectTrigger, {
			...forwardAccessibleName(accessibleName),
			variant,
			state,
			sizing,
			placeholder,
			className,
			onBlur,
			"aria-describedby": ariaDescribedBy,
			"aria-invalid": ariaInvalid,
			"aria-required": ariaRequired
		}), /* @__PURE__ */ jsx(SelectContent, { children: items.map((itemOrGroup, index) => {
			if (isGroup(itemOrGroup)) return /* @__PURE__ */ jsxs(SelectGroup, { children: [itemOrGroup.label && /* @__PURE__ */ jsx(SelectLabel, { children: itemOrGroup.label }), itemOrGroup.items.map(renderItem)] }, `${groupKeyId}-group-${index}`);
			return renderItem(itemOrGroup);
		}) })]
	});
};
Select.Root = SelectRoot;
Select.Trigger = SelectTrigger;
Select.Content = SelectContent;
Select.Item = SelectItem;
Select.Group = SelectGroup;
Select.Label = SelectLabel;
Select.Separator = SelectSeparator;
//#endregion
export { Select };

//# sourceMappingURL=Select-B4uK0QVm.js.map